import { AppContext } from '@layouts/LayoutAdmin/LayoutAdmin';
import { EUserRoles } from '@models/user';
import { Form } from 'antd';
import { Table } from 'antd/lib';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EModalType } from 'types/others';
import { getColumns } from '../columns';
import useCompanyApi from './useCompanyApi';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, images } from '@constants';
import {
  TFormCompanyReq,
  TFormModalRegisterReq,
  TFormModalRes,
  TListCompanyReq,
  TListCompanyTable,
  TSelectValue,
} from '@models/company';
import { COMPANY_CONSTANT, REGEX_COMPANY_CODE } from '../constants';
import { TSearchCompanyParam } from 'types/types';
import { COMMON_CONSTANT, USER_ROLE } from '@utils/constants';
import { useCurrentUserRole } from '@hooks';

const { NAME, TITLE, MESSAGE } = COMPANY_CONSTANT;

const ADMIN_ROLES = [EUserRoles.ADMIN, EUserRoles.STORE_OWNER];

const computeIsRoleAdmin = (role: EUserRoles[] | undefined) =>
  role ? ADMIN_ROLES.includes(role[0]) : false;

const computeDisableBtnNext = (
  modalType: EModalType | null | string,
  companyCodeWatch: string,
  companyNameWatch: string
) => {
  const isNotDetailModal = modalType !== EModalType.DETAIL;
  const isCompanyCodeMissing = !companyCodeWatch;
  const isCompanyNameMissing = !companyNameWatch;
  const isCompanyNameBlank =
    Boolean(companyNameWatch) && !companyNameWatch.replace(/\s/g, '').length;
  const isFormInvalid =
    isCompanyCodeMissing || isCompanyNameMissing || isCompanyNameBlank;

  return isNotDetailModal && isFormInvalid;
};

const computeDisableFields = (modalType: EModalType | null | string) =>
  modalType === EModalType.CONFIRM_REGISTRATION ||
  modalType === EModalType.CONFIRM_UPDATE ||
  modalType === EModalType.DETAIL;

const computeDisableFieldsEdit = (
  modalType: EModalType | null | string,
  disableFields: boolean
) => modalType === EModalType.UPDATE || disableFields;

const buildCompanyModalTextMap = (regisFailMsg: string) => ({
  [EModalType.REGISTRATION]: {
    title: TITLE.COMPANY_REGISTRATION,
    okText: TITLE.BTN_NEXT,
    message: '',
    image: undefined,
  },
  [EModalType.DETAIL]: {
    title: TITLE.COMPANY_DETAIL,
    okText: TITLE.BTN_CLOSE,
    message: '',
    image: undefined,
  },
  [EModalType.UPDATE]: {
    title: TITLE.COMPANY_UPDATE,
    okText: TITLE.BTN_NEXT,
    message: '',
    image: undefined,
  },
  [EModalType.CONFIRM_REGISTRATION]: {
    title: TITLE.COMPANY_REGISTRATION,
    okText: TITLE.BTN_MODAL_REGIST,
    message: '',
    image: undefined,
  },
  [EModalType.CONFIRM_UPDATE]: {
    title: TITLE.COMPANY_UPDATE,
    okText: TITLE.OK_UPDATE,
    message: '',
    image: undefined,
  },
  [EModalType.SUCCESS_REGISTRATION]: {
    title: TITLE.COMPANY_LIST_REGISTRATION,
    okText: TITLE.BTN_OK,
    message: MESSAGE.SUCCESS_REGISTER,
    image: images.confirmApproveTemperature,
  },
  [EModalType.SUCCESS_UPDATE]: {
    title: TITLE.COMPANY_LIST_UPDATE,
    okText: TITLE.BTN_OK,
    message: MESSAGE.SUCCESS_UPDATE,
    image: images.confirmApproveTemperature,
  },
  [EModalType.SUCCESS_DELETE]: {
    title: TITLE.SUCCESS_DELETE,
    okText: TITLE.BTN_OK,
    message: MESSAGE.SUCCESS_DELETE,
    image: images.confirmApproveTemperature,
  },
  [EModalType.FAIL_REGISTRATION]: {
    title: TITLE.FAIL_REGISTER,
    okText: TITLE.BTN_OK,
    message: regisFailMsg,
    image: images.importDataFail,
  },
  [EModalType.FAIL_UPDATE]: {
    title: TITLE.FAIL_REGISTER,
    okText: TITLE.BTN_OK,
    message: MESSAGE.FAIL_UPDATE,
    image: images.importDataFail,
  },
  [EModalType.FAIL_DELETE]: {
    title: TITLE.FAIL_DELETE,
    okText: TITLE.BTN_OK,
    message: MESSAGE.FAIL_DELETE,
    image: images.importDataFail,
  },
  [TITLE.NOT_FOUND_COMPANY]: {
    title: TITLE.FAIL_DELETE,
    okText: TITLE.BTN_OK,
    message: MESSAGE.NOT_FOUND_COMPANY,
    image: images.importDataFail,
  },
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
    .COMMON_CHECK_FOUND_BEFORE_UPDATE]: {
    title: TITLE.FAIL_REGISTER,
    okText: TITLE.BTN_OK,
    message: MESSAGE.CHECK_BEFORE_UPDATE,
    image: images.importDataFail,
  },
});

const DEFAULT_COMPANY_MODAL_TEXT = {
  title: '',
  okText: '',
  message: '',
  image: undefined,
};

function useCompanyPage() {
  const currentUserRole = useCurrentUserRole();
  const [form] = Form.useForm();
  const [formModal] = Form.useForm();
  const companyCodeWatch = Form.useWatch(NAME.COMPANY_CODE, formModal);
  const companyNameWatch = Form.useWatch(NAME.COMPANY_NAME, formModal);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [listCompany, setListCompany] = useState<TListCompanyTable[]>([]);
  const [companies, setCompanies] = useState<TSelectValue[]>([]);
  const [totalRecords, setTotalRecords] = useState(0); // records table
  const [pageIndex, setPageIndex] = useState(0);
  const [rowKey, setRowKey] = useState<number | null>(null);
  const [recordId, setRecordId] = useState('');

  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingExist, setLoadingExist] = useState(false);
  const [companiesModal, setCompaniesModal] = useState<TSelectValue[]>([]);
  const [company, setCompany] = useState<TListCompanyTable | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalType, setModalType] = useState<EModalType | null | string>(null);
  const [modalTypeConfirm, setModalTypeConfirm] = useState<EModalType | null>(
    null
  );
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [regisFailMsg, setRegisFailMsg] = useState(MESSAGE.FAIL_REGISTER);

  const [searchParams, setSearchParams] = useSearchParams();
  const companyParam = searchParams.get(NAME.COMPANY) || '';

  const { handler, effect } = useCompanyApi();
  const { role } = useContext(AppContext);

  const isRoleAdmin = computeIsRoleAdmin(role);
  const disableBtnOther = isRoleAdmin ? false : true;

  const disableBtnNext = computeDisableBtnNext(
    modalType,
    companyCodeWatch,
    companyNameWatch
  );

  const disableFields = computeDisableFields(modalType);

  const disableFieldsEdit = computeDisableFieldsEdit(modalType, disableFields);

  const tblRef: Parameters<typeof Table>[0]['ref'] = useRef(null);

  const handleSetFormValues = (record: TFormModalRes) => {
    setRecordId(record.id);

    formModal.setFieldValue(NAME.COMPANY_CODE, record.companyCode);
    formModal.setFieldValue(NAME.COMPANY_NAME, record.companyName);
  };

  const handleSetSearchParams = (values: TSearchCompanyParam) => {
    searchParams.set(NAME.COMPANY_ID, values.companyId || '');

    setSearchParams(searchParams, { replace: true });
  };

  // Modal success
  const handleCloseModalSuccess = () => {
    setModalSuccess(false);
    setModalType(null);
    setRowKey(null);
  };

  const handleModalSuccess = () => {
    setModalSuccess(true);
    setModalType(null);

    if (modalTypeConfirm === EModalType.CONFIRM_REGISTRATION_FINAL) {
      setModalType(EModalType.SUCCESS_REGISTRATION);
    }

    if (modalTypeConfirm === EModalType.CONFIRM_UPDATE_FINAL) {
      setModalType(EModalType.SUCCESS_UPDATE);
    }
  };

  // Modal fail
  const handleModalFail = (description?: string) => {
    if (
      description &&
      description ===
        COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_ALREADY_EXISTED
    ) {
      setRegisFailMsg(MESSAGE.VALUE_EXIST);
    }

    if (
      description &&
      description ===
        COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_NOT_EXIST
    ) {
      setModalSuccess(true);
      setModalType(TITLE.NOT_FOUND_COMPANY);
      return;
    }

    setModalSuccess(true);

    if (modalTypeConfirm === EModalType.CONFIRM_REGISTRATION_FINAL) {
      setModalType(EModalType.FAIL_REGISTRATION);
    }

    if (modalTypeConfirm === EModalType.CONFIRM_UPDATE_FINAL) {
      setModalType(EModalType.FAIL_UPDATE);
    }
  };

  const handleCommonError = () => {
    setModalConfirm(false);
    setModalType(null);
    setCompany(null);
    setRowKey(null);
    setModalOpen(false);
  };

  const handleDeleteFail = () => {
    setModalSuccess(true);
    setModalConfirm(false);
    setModalDelete(false);
    setRowKey(null);
  };

  // Modal confirm final
  const handleOpenModalConfirmFinal = () => {
    setModalConfirm(true);
    if (modalType === EModalType.CONFIRM_REGISTRATION) {
      setModalTypeConfirm(EModalType.CONFIRM_REGISTRATION_FINAL);
    }

    if (modalType === EModalType.CONFIRM_UPDATE) {
      setModalTypeConfirm(EModalType.CONFIRM_UPDATE_FINAL);
    }
  };

  const handleOkModalConfirmFinal = () => {
    handleOkModal();
  };

  const handleCloseModalConfirmFinal = () => setModalConfirm(false);

  // Modal registration
  const handleOpenModalRegistration = () => {
    setModalOpen(true);
    setModalType(EModalType.REGISTRATION);
  };

  // Modal detail
  const handleOpenModalDetail = (record: TListCompanyTable) => {
    setModalOpen(true);
    setModalType(EModalType.DETAIL);
    setRowKey(record.key);

    handler.detailCompany(record.id, handleSetFormValues, setLoadingModal);
  };

  // Modal update
  const handleOpenModalUpdate = (record: TListCompanyTable, id: string) => {
    setModalOpen(true);
    setModalType(EModalType.UPDATE);
    setRowKey(record.key);

    handler.detailCompany(id, handleSetFormValues, setLoadingModal);
  };

  const handleCloseModal = () => {
    setModalType(null);
    setCompany(null);
    setRowKey(null);
    setModalOpen(false);
  };

  const handleCheckFoundBeforeUpdate = () => {
    setModalOpen(false);
    setModalSuccess(true);
    setModalType(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
        .COMMON_CHECK_FOUND_BEFORE_UPDATE
    );
  };

  const handleFormResetFieldError = () => {
    formModal.setFields([
      {
        name: [NAME.COMPANY_CODE],
        errors: [],
      },
      {
        name: [NAME.COMPANY_NAME],
        errors: [],
      },
    ]);
  };

  const handleValidateForm = () => {
    console.log(recordId, 'recordId');

    handleFormResetFieldError();

    const validateCompanyCode = companyCodeWatch
      ? REGEX_COMPANY_CODE.test(companyCodeWatch.trim())
      : '';
    const _validateCompanyCode =
      !validateCompanyCode && validateCompanyCode !== '';

    let checkError = false;

    if (_validateCompanyCode) {
      formModal.setFields([
        {
          name: [NAME.COMPANY_CODE],
          errors: [MESSAGE.FIELD_INVALID_FORMAT],
        },
      ]);
      checkError = true;
    }

    if (!checkError) {
      formModal.setFields([{ name: [NAME.COMPANY_CODE], errors: [] }]);

      const companyCode = formModal.getFieldValue(NAME.COMPANY_CODE);
      if (modalType === EModalType.REGISTRATION) {
        handler.checkExistCompany(
          {
            companyCode,
            typeCheck: 'CREATE',
          },
          formModal,
          EModalType.CONFIRM_REGISTRATION,
          setModalType,
          setLoadingExist,
          handleCheckFoundBeforeUpdate
        );
      }

      if (modalType === EModalType.UPDATE) {
        handler.checkExistCompany(
          {
            companyCode,
            companyId: recordId,
            typeCheck: 'UPDATE',
          },
          formModal,
          EModalType.CONFIRM_UPDATE,
          setModalType,
          setLoadingExist,
          handleCheckFoundBeforeUpdate
        );
      }
    }
  };

  // Modal delete company
  const handleOpenModalDelete = (record: TListCompanyTable) => {
    setCompany(record);
    setRowKey(record.key);
    setModalDelete(true);
  };

  const handleCloseModalDelete = () => {
    setCompany(null);
    setRowKey(null);
    setModalDelete(false);
  };

  const handleFinish = (values: TListCompanyReq) => {
    setListCompany([]);
    setPageIndex(1);

    getListCompanySearch({
      ...values,
      page: DEFAULT_PAGE_NUMBER,
    });
  };

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      listCompany.length < totalRecords
    ) {
      setPageIndex((prev) => prev + 1);
      getListCompanyScroll({
        companyId: companyParam,
        page: pageIndex + 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    }
  };

  const handleOkModalSuccess = () => {
    setModalOpen(false);
    handleModalSuccess();
    handleCloseModalConfirmFinal();
    setListCompany([]);
    setPageIndex(1);
    getListCompanySearch({
      companyId: companyParam,
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    });
  };

  const renderTextModal = useMemo(() => {
    const modalTextMap = buildCompanyModalTextMap(regisFailMsg);

    if (modalType && modalType in modalTextMap) {
      return modalTextMap[modalType as keyof typeof modalTextMap];
    }

    return DEFAULT_COMPANY_MODAL_TEXT;
  }, [modalType]);

  const renderTextModalConfirm = useMemo(() => {
    switch (modalTypeConfirm) {
      case EModalType.CONFIRM_REGISTRATION_FINAL:
        return {
          title: TITLE.CONFIRM_REGISTRATION_FINAL,
          okText: TITLE.BTN_OK,
          message: MESSAGE.CONFIRM_REGISTRATION_FINAL,
        };
      case EModalType.CONFIRM_UPDATE_FINAL:
        return {
          title: TITLE.CONFIRM_UPDATE_FINAL,
          okText: TITLE.BTN_OK,
          message: MESSAGE.CONFIRM_UPDATE_FINAL,
        };

      default:
        return {
          title: '',
          okText: '',
          message: '',
        };
    }
  }, [modalTypeConfirm]);

  // API
  const handleOkModalDelete = async () => {
    if (company) {
      handler.deleteCompany(
        company.id,
        listCompany,
        setListCompany,
        setLoadingPage,
        setModalSuccess,
        setModalType,
        handleCloseModalDelete,
        handleDeleteFail
      );
    }
  };

  const handleOkModal = () => {
    const params: TFormModalRegisterReq = {
      companyCode: companyCodeWatch,
      companyName: companyNameWatch,
    };

    if (modalTypeConfirm === EModalType.CONFIRM_REGISTRATION_FINAL) {
      handler.registrationCompany(
        params,
        handleOkModalSuccess,
        setLoadingPage,
        handleModalFail,
        handleCommonError
      );
    }

    if (modalTypeConfirm === EModalType.CONFIRM_UPDATE_FINAL) {
      handler.updateCompany(
        { ...params, id: recordId },
        handleOkModalSuccess,
        setLoadingPage,
        handleModalFail,
        handleCommonError
      );
    }
  };

  const getListCompanySearch = async (payload: TListCompanyReq) => {
    handler.getListCompanySearch(
      payload,
      setLoadingPage,
      setListCompany,
      handleSetSearchParams,
      setPageIndex,
      setTotalRecords
    );
  };

  const getListCompanyScroll = async (payload: TListCompanyReq) => {
    handler.getListCompanyScroll(
      payload,
      listCompany,
      pageIndex,
      setLoadingPage,
      setListCompany,
      setPageIndex,
      setTotalRecords
    );
  };

  const fetchInitialApis = async () => {
    handler.fetchInitialApis(
      pageIndex,
      setLoadingPage,
      setLoadingCompany,
      handleSetSearchParams,
      setCompanies,
      setListCompany,
      setCompaniesModal,
      setTotalRecords,
      setPageIndex
    );
  };

  const fetchApiRedirect = async (payload: TFormCompanyReq) => {
    handler.fetchApiRedirect(
      payload,
      pageIndex,
      setLoadingPage,
      setLoadingCompany,
      handleSetSearchParams,
      setCompanies,
      setListCompany,
      setCompaniesModal,
      setTotalRecords,
      setPageIndex
    );
  };

  const columns = useMemo(() => {
    const columns = getColumns({
      disableBtnOther,
      handleOpenModalDelete,
      handleOpenModalDetail,
      handleOpenModalUpdate,
    });
    return columns;
  }, [companiesModal]);

  // EFFECT
  useEffect(() => {
    if (currentUserRole !== USER_ROLE.STORE_OWNER) {
      if (companyParam) {
        fetchApiRedirect({
          companyId: companyParam,
          pageSize: DEFAULT_PAGE_SIZE,
        });
      } else {
        fetchInitialApis();
      }
    }
  }, []);

  useEffect(() => {
    effect.effectCompany(companyParam, form);
  }, [companies]);

  return {
    state: {
      columns,
      listCompany,
      company,
      loadingPage,
      rowKey,
      tblRef,
      form,
      companies,
      loadingCompany,

      disableBtnOther,
      disableBtnNext,
      disableFields,
      disableFieldsEdit,

      loadingExist,
      loadingModal,
      companiesModal,
      formModal,
      renderTextModal,
      renderTextModalConfirm,
      modalSuccess,
      modalOpen,
      modalDelete,
      modalType,
      modalConfirm,
    },
    handler: {
      setModalType,
      handleFinish,
      handleScroll,
      handleOpenModalRegistration,
      handleCloseModal,
      handleCloseModalDelete,
      handleOkModalDelete,
      handleOkModal,
      handleCloseModalSuccess,
      handleOpenModalConfirmFinal,
      handleValidateForm,
      handleCloseModalConfirmFinal,
      handleOkModalConfirmFinal,
    },
  };
}

export default useCompanyPage;
